import React, { useEffect, useState } from "react";
import AdminLayout from "./Common/AdminLayout";
import LoadingSkeleton from "./Common/LoadingSkeleton";
import { FaEdit, FaPlus, FaTimes } from "react-icons/fa";
import { AiFillDelete } from "react-icons/ai";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import {
  AddJobType,
  FetchJobTypes,
  EditJobType,
  UpdateJobType,
  DeleteJobTypes,
} from "../../Redux/Slices/JobType";
import "../../asset/new/css/admin-modern.css";

const JobType = () => {
  const [jobTypeName, setJobTypeName] = useState("");
  const [editData, setEditData] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const dispatch = useDispatch();
  const { jobTypesData } = useSelector((state) => state?.JobType);

  //Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!jobTypeName.trim()) {
      toast.error("Please enter job type name");
      return;
    }
    let inputData = { name: jobTypeName };
    await AddJobType(inputData);
    dispatch(FetchJobTypes());
    setJobTypeName("");
    setAddModalOpen(false);
    toast.success("Job type added successfully");
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  // Edit
  const handleEdit = async (id) => {
    const data = await EditJobType(id);
    setEditData(data?.data[0]);
    setEditModalOpen(true);
  };

  //Update
  const handleUpdate = async (id) => {
    if (!editData?.name?.trim()) {
      toast.error("Please enter job type name");
      return;
    }
    let inputData = { name: editData?.name };
    await UpdateJobType(inputData, id);
    dispatch(FetchJobTypes());
    setEditModalOpen(false);
    toast.success("Job type updated successfully");
  };

  // Delete
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this job type?")) return;
    await DeleteJobTypes(id);
    dispatch(FetchJobTypes());
    toast.success("Job type deleted successfully");
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await dispatch(FetchJobTypes());
      setLoading(false);
    };
    fetchData();
    document.title = "Job Types | Admin";
  }, [dispatch]);

  // Format the date
  const formatDate = (dateString) => {
    const dateObject = new Date(dateString);
    const formattedDate = dateObject
      .toISOString()
      .replace("T", " ")
      .replace("Z", "");
    return formattedDate.slice(0, -5);
  };

  return (
    <AdminLayout
      title="Job Types"
      subtitle="Manage employment types"
      actions={
        <button
          onClick={() => setAddModalOpen(true)}
          className="admin-btn admin-btn-primary"
        >
          <FaPlus className="me-2" />
          Add Job Type
        </button>
      }
    >
      {/* Search Bar */}
      <div className="admin-card" style={{ marginBottom: 'var(--spacing-xl)' }}>
        <div className="admin-card-body">
          <input
            type="search"
            placeholder="Search job types..."
            className="admin-input"
            value={searchQuery}
            onChange={(e) => handleSearch(e)}
            style={{ maxWidth: "500px" }}
          />
        </div>
      </div>

      {/* Table Card */}
      <div className="admin-card">
        <div className="admin-card-body">
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Job Type</th>
                  <th>Created At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <LoadingSkeleton type="table-row" count={5} columns={4} />
                ) : jobTypesData?.length > 0 ? (
                  jobTypesData
                    .filter((item) =>
                      item.name
                        .toLowerCase()
                        .includes(searchQuery.toLowerCase())
                    )
                    .map((item, index) => (
                      <tr key={item._id}>
                        <td>
                          <span className="admin-badge admin-badge-light">
                            {index + 1}
                          </span>
                        </td>
                        <td>{item?.name}</td>
                        <td>{formatDate(item?.created_at)}</td>
                        <td>
                          <div className="admin-btn-group">
                            <button
                              onClick={() => handleEdit(item?._id)}
                              className="admin-btn admin-btn-sm admin-btn-warning"
                              title="Edit"
                            >
                              <FaEdit />
                            </button>
                            <button
                              onClick={() => handleDelete(item?._id)}
                              className="admin-btn admin-btn-sm admin-btn-danger"
                              title="Delete"
                            >
                              <AiFillDelete />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                ) : (
                  <tr>
                    <td colSpan="4">
                      <div className="admin-empty-state">
                        <i className="fas fa-briefcase fa-3x mb-3"></i>
                        <h4>No Job Types Found</h4>
                        <p>Start by adding your first job type.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Add Modal */}
      {addModalOpen && (
        <div className="admin-modal-backdrop" onClick={() => setAddModalOpen(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Add Job Type</h3>
              <button onClick={() => setAddModalOpen(false)} className="admin-modal-close">
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="admin-modal-body">
                <div className="admin-form-group">
                  <label className="admin-form-label">Job Type Name</label>
                  <input
                    type="text"
                    className="admin-input"
                    placeholder="Enter job type name"
                    value={jobTypeName}
                    onChange={(e) => setJobTypeName(e.target.value)}
                    autoFocus
                  />
                </div>
              </div>
              <div className="admin-modal-footer">
                <button
                  type="button"
                  onClick={() => setAddModalOpen(false)}
                  className="admin-btn admin-btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="admin-btn admin-btn-primary">
                  Add Job Type
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModalOpen && editData && (
        <div className="admin-modal-backdrop" onClick={() => setEditModalOpen(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Edit Job Type</h3>
              <button onClick={() => setEditModalOpen(false)} className="admin-modal-close">
                <FaTimes />
              </button>
            </div>
            <div className="admin-modal-body">
              <div className="admin-form-group">
                <label className="admin-form-label">Job Type Name</label>
                <input
                  type="text"
                  className="admin-input"
                  placeholder="Enter job type name"
                  value={editData?.name || ""}
                  onChange={(e) =>
                    setEditData({ ...editData, name: e.target.value })
                  }
                  autoFocus
                />
              </div>
            </div>
            <div className="admin-modal-footer">
              <button
                onClick={() => setEditModalOpen(false)}
                className="admin-btn admin-btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => handleUpdate(editData._id)}
                className="admin-btn admin-btn-primary"
              >
                Update Job Type
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default JobType;
